/**
 * Pure autosave state machine for the quote-document editor, shared so it can
 * be unit-tested without a DOM, timers, or the network.
 *
 * The editor's source of truth is the stored HTML document string. Every
 * mutation funnels through one history-commit point; each commit is an `edit`
 * event here. The machine debounces edits (the timer lives in the caller),
 * runs a single in-flight PUT of `{ document, expectedVersion }`, and coalesces
 * any edits made mid-flight into the next save. It never merges: a 409 conflict
 * is surfaced for explicit reload-or-keep recovery, and a 400 validation
 * rejection becomes a persistent error while the local document is kept intact.
 *
 * The state carries the version the next save is based on, the last document
 * confirmed saved (so "dirty" is a string compare), the document a currently
 * in-flight save is persisting, an optional pending conflict awaiting the
 * user's choice, and an optional persistent save error. It can also become
 * permanently `locked` when the server reports the quote was finalized
 * mid-edit: neither a version conflict nor a retryable rejection, a locked state
 * derives no further save or flush and the caller surfaces a read-only notice
 * rather than the reload-or-keep dialog. The caller reads `deriveNextAction` to
 * learn what side effect to run after each transition.
 */

/** A conflict awaiting the user's reload-or-keep choice after a 409. */
export interface AutosaveConflict {
  /** The server's current document HTML at the time of the conflict. */
  serverDocument: string;
  /** The server's current version the next save must be based on. */
  serverVersion: number;
}

/** The pure autosave state owned by the editor's autosave hook. */
export interface AutosaveState {
  /** The version the next save is based on (the last confirmed version). */
  version: number;
  /** The last document HTML confirmed saved by the server. */
  savedDocument: string;
  /** The latest document HTML the user has produced. */
  document: string;
  /** The document a currently in-flight save is persisting, or null. */
  inFlightDocument: string | null;
  /** A 409 conflict awaiting the user's reload-or-keep choice, or null. */
  conflict: AutosaveConflict | null;
  /** A persistent save error (e.g. a 400 validation message), or null. */
  error: string | null;
  /**
   * The document a 400 rejection last refused, or null. While the working
   * document still equals it, no save is scheduled - the caller waits for the
   * user to edit into something the server has not already refused.
   */
  rejectedDocument: string | null;
  /**
   * Whether the quote was finalized on the server mid-edit, permanently locking
   * the document. Terminal: no save or flush is derived and the caller shows a
   * read-only notice instead of the reload-or-keep dialog.
   */
  locked: boolean;
}

/** The side effect the caller should run after the latest transition. */
export type AutosaveAction =
  | {
      /** Start a debounced save of `document` against `expectedVersion`. */
      kind: "schedule-save";
      document: string;
      expectedVersion: number;
    }
  | {
      /** Nothing to do: clean, saving already in flight, or blocked. */
      kind: "idle";
    };

/** A final, immediate save to run before the editor unmounts. */
export type AutosaveFlushAction =
  | {
      /** Fire one last PUT of `document` against `expectedVersion`. */
      kind: "flush-save";
      document: string;
      expectedVersion: number;
    }
  | {
      /** Nothing pending: clean, blocked by a conflict, or already refused. */
      kind: "idle";
    };

/**
 * Creates the initial autosave state seeded from the document the editor
 * opened with and the version it was read at.
 * @param document - The stored document HTML the editor seeded from
 * @param version - The optimistic-concurrency version the HTML was read at
 * @returns A clean autosave state with no in-flight save or error
 */
export function createAutosaveState(
  document: string,
  version: number,
): AutosaveState {
  return {
    version,
    savedDocument: document,
    document,
    inFlightDocument: null,
    conflict: null,
    error: null,
    rejectedDocument: null,
    locked: false,
  };
}

/**
 * Records a new document produced by a history commit. Clears any prior
 * persistent error so a fresh edit gets a fresh save attempt; a pending
 * conflict is left in place until the user resolves it.
 * @param state - The current autosave state
 * @param document - The new document HTML from the commit
 * @returns The next autosave state
 */
export function onEdit(state: AutosaveState, document: string): AutosaveState {
  if (document === state.document) {
    return state;
  }
  return { ...state, document, error: null, rejectedDocument: null };
}

/**
 * Marks a save as started for the given document, moving it in-flight so the
 * caller runs at most one PUT at a time.
 * @param state - The current autosave state
 * @param document - The document HTML the started save is persisting
 * @returns The next autosave state
 */
export function onSaveStarted(
  state: AutosaveState,
  document: string,
): AutosaveState {
  return { ...state, inFlightDocument: document, error: null };
}

/**
 * Applies a successful save: bumps to the server's new version and marks the
 * saved document. Edits made while the save was in flight remain pending, so
 * the document can still be dirty against the new version afterwards.
 * @param state - The current autosave state
 * @param savedDocument - The document HTML the completed save persisted
 * @param version - The new optimistic-concurrency version from the server
 * @returns The next autosave state
 */
export function onSaveSucceeded(
  state: AutosaveState,
  savedDocument: string,
  version: number,
): AutosaveState {
  return {
    ...state,
    version,
    savedDocument,
    inFlightDocument: null,
    error: null,
    rejectedDocument: null,
  };
}

/**
 * Applies a 400-style validation rejection: clears the in-flight save and
 * records a persistent error while keeping the local document intact. The
 * error clears on the next edit, which retries the save.
 * @param state - The current autosave state
 * @param message - The server's validation error message
 * @param document - The document HTML the server refused
 * @returns The next autosave state
 */
export function onSaveRejected(
  state: AutosaveState,
  message: string,
  document: string,
): AutosaveState {
  return {
    ...state,
    inFlightDocument: null,
    error: message,
    rejectedDocument: document,
  };
}

/**
 * Applies a transient save failure (a network error or a 5xx): clears the
 * in-flight save and records a retryable error WITHOUT marking the document
 * rejected, so the same unchanged document is scheduled again rather than
 * waiting for the user to edit.
 * @param state - The current autosave state
 * @param message - The failure message to surface
 * @returns The next autosave state
 */
export function onSaveFailed(
  state: AutosaveState,
  message: string,
): AutosaveState {
  return { ...state, inFlightDocument: null, error: message };
}

/**
 * Records a 409 conflict, parking the server's current document and version
 * for the user's explicit reload-or-keep choice. No save runs while a conflict
 * is pending.
 * @param state - The current autosave state
 * @param conflict - The server's current document and version
 * @returns The next autosave state
 */
export function onSaveConflict(
  state: AutosaveState,
  conflict: AutosaveConflict,
): AutosaveState {
  return { ...state, inFlightDocument: null, conflict };
}

/**
 * Records that the quote was finalized on the server mid-edit: the document is
 * now permanently locked. Clears any in-flight save, pending conflict, and
 * transient error, and blocks all further save and flush actions. Terminal -
 * only reopening the editor against an editable quote leaves this state.
 *
 * When the stored document is supplied it is adopted as the working document,
 * so the read-only view shows what the finalized quote actually uses rather
 * than local edits that were never persisted.
 * @param state - The current autosave state
 * @param server - The stored document and version to adopt, when it could be read
 * @returns The next autosave state, permanently locked
 */
export function onSaveLocked(
  state: AutosaveState,
  server?: AutosaveConflict,
): AutosaveState {
  const adopted = server
    ? {
        version: server.serverVersion,
        savedDocument: server.serverDocument,
        document: server.serverDocument,
      }
    : {};
  return {
    ...state,
    ...adopted,
    inFlightDocument: null,
    conflict: null,
    error: null,
    rejectedDocument: null,
    locked: true,
  };
}

/**
 * Resolves a pending conflict by reloading the server's version: the working
 * document becomes the server HTML and the next save is based on the server
 * version. The caller resets local undo/redo history alongside this.
 * @param state - The current autosave state
 * @returns The next autosave state, clean against the server version
 */
export function resolveConflictWithReload(state: AutosaveState): AutosaveState {
  if (!state.conflict) {
    return state;
  }
  return {
    version: state.conflict.serverVersion,
    savedDocument: state.conflict.serverDocument,
    document: state.conflict.serverDocument,
    inFlightDocument: null,
    conflict: null,
    error: null,
    rejectedDocument: null,
    locked: false,
  };
}

/**
 * Resolves a pending conflict by keeping the local document: adopts the
 * server's version to save against so the next PUT overwrites the server copy,
 * accepting the rare single-editor clobber.
 * @param state - The current autosave state
 * @returns The next autosave state, keeping local HTML at the server version
 */
export function resolveConflictWithKeep(state: AutosaveState): AutosaveState {
  if (!state.conflict) {
    return state;
  }
  return {
    ...state,
    version: state.conflict.serverVersion,
    conflict: null,
    error: null,
    rejectedDocument: null,
  };
}

/**
 * Whether the working document differs from the last confirmed-saved document.
 * @param state - The current autosave state
 * @returns True when there are unsaved edits pending
 */
export function isDirty(state: AutosaveState): boolean {
  return state.document !== state.savedDocument;
}

/**
 * Derives the side effect to run after the latest transition. A save is
 * scheduled only when the document is dirty, nothing is in flight, and no
 * conflict is pending; otherwise the caller stays idle. Edits made during an
 * in-flight save are naturally picked up: when that save resolves, the next
 * derive sees the still-dirty document and schedules the coalesced save. A
 * document the server just rejected (400) stays idle until the user edits it,
 * and a locked state (the quote was finalized) never schedules a save again.
 * @param state - The current autosave state
 * @returns The action the caller should run
 */
export function deriveNextAction(state: AutosaveState): AutosaveAction {
  if (
    state.locked ||
    state.conflict !== null ||
    state.inFlightDocument !== null ||
    state.document === state.savedDocument ||
    state.document === state.rejectedDocument
  ) {
    return { kind: "idle" };
  }
  return {
    kind: "schedule-save",
    document: state.document,
    expectedVersion: state.version,
  };
}

/**
 * Derives the final save to run when the editor unmounts before its debounce
 * fires, so the last edits are not silently dropped. A flush is requested only
 * when the working document is dirty, no conflict is pending, and the document
 * is not one the server already refused. It is also derived while a save is in
 * flight, because the working document is the newest HTML and covers the queued
 * edit the coalescing loop would otherwise have sent once that save resolved -
 * but `expectedVersion` is only valid when nothing is in flight. The caller must
 * wait for any in-flight save and use the version it returns, otherwise the two
 * PUTs race and the newer one loses to the optimistic-concurrency guard. Any
 * 409/400 on the detached save is a noop, since the UI is gone.
 * A locked state (the quote was finalized) never flushes: further writes are
 * forbidden and would only 409 again.
 * @param state - The current autosave state
 * @returns The flush action the caller should run on unmount
 */
export function deriveFlushAction(state: AutosaveState): AutosaveFlushAction {
  if (
    state.locked ||
    state.conflict !== null ||
    state.document === state.savedDocument ||
    state.document === state.rejectedDocument
  ) {
    return { kind: "idle" };
  }
  return {
    kind: "flush-save",
    document: state.document,
    expectedVersion: state.version,
  };
}
